/**
 * How the fan page keeps its live settings honest once a kiosk serves the seat.
 *
 * get_fan_page_data hands the page one `settings` object: the tenant's
 * club_settings with the serving kiosk's operator_settings overlaid, and
 * `ordering_enabled` replaced by "is ordering visible here" (the tenant's own
 * kiosk follows the tenant's flag; anyone else's kiosk — a caterer, picked or
 * the venue default — shows unless the tenant hid it; since 2026-10-14 by
 * ownership, not by how the kiosk was resolved). Realtime then pushes RAW
 * rows, and a club_settings row still
 * carries legacy copies of the kiosk switches plus the tenant's own
 * ordering_enabled — merging it blindly would revert the kiosk's pause or hide
 * the venue's menu. These helpers reproduce the RPC's overlay on every patch.
 */

/** The operator_settings columns the RPC overlays onto `settings` (its own bookkeeping excluded). */
export const OPERATOR_OVERLAY_KEYS = [
  'payment_mode',
  'service_fee_feature_enabled',
  'service_fee_cents',
  'ordering_paused',
  'order_received_notice',
  'giveaway_enabled',
  'giveaway_prize',
  'order_comments_enabled',
  'preorder_enabled',
  'preorder_cutoff_minute',
  'pickup_now_enabled',
] as const;

const OVERLAY_KEY_SET: ReadonlySet<string> = new Set(OPERATOR_OVERLAY_KEYS);

type Patch = Record<string, unknown>;

/** A club_settings patch without the legacy copies of the kiosk switches. */
export function withoutOperatorKeys<T extends object>(patch: T): Partial<T> {
  const out: Patch = {};
  for (const [key, value] of Object.entries(patch)) {
    if (!OVERLAY_KEY_SET.has(key)) out[key] = value;
  }
  return out as Partial<T>;
}

/**
 * Merge a realtime club_settings row into the live settings. With a kiosk
 * present, the kiosk's switches stand and `ordering_enabled` is recomputed
 * the way the RPC computes it; without one, this is the legacy plain merge.
 * `T` is the settings shape (an interface, hence `object` not `Record`).
 */
export function mergeClubSettingsPatch<T extends object>(
  prev: T | null,
  patch: Partial<T>,
  operator: { source: 'override' | 'default'; own?: boolean } | null,
): T {
  if (!operator) return { ...(prev ?? {}), ...patch } as T;
  const safe = withoutOperatorKeys(patch);
  const merged: Patch = { ...(prev ?? {}), ...safe };
  // Someone else's kiosk: the tenant's own switch is irrelevant, only "hidden"
  // counts. A payload without `own` (older RPC) falls back to the source rule.
  const own = operator.own ?? operator.source !== 'default';
  if (!own) {
    const patched = patch as Patch;
    const previous = (prev ?? {}) as Patch;
    const hidden = (patched.venue_ordering_hidden ?? previous.venue_ordering_hidden ?? false) as boolean;
    merged.ordering_enabled = !hidden;
  }
  return merged as T;
}

/** Merge a realtime operator_settings row into the live settings, as the RPC overlays it. */
export function mergeOperatorSettingsPatch<T extends object>(prev: T | null, patch: Patch): T {
  const rest: Patch = {};
  for (const [key, value] of Object.entries(patch)) {
    if (key !== 'operator_id' && key !== 'updated_at') rest[key] = value;
  }
  return { ...(prev ?? {}), ...rest } as T;
}
